/**
 * Export the current focal space as a raster image — PNG download or PNG
 * to the clipboard. (Vector SVG export lives in exportSvg.ts and renders
 * headless from the scene model instead of capturing the DOM.)
 *
 * Captures the inner content div (the one carrying the pan/zoom
 * transform) with the transform neutralized, so the result is the focal
 * space at its native item-coordinate resolution rather than the user's
 * current pan/zoom.
 */
import { domToCanvas } from "modern-screenshot";
import type { BoardSpace } from "@archlang/scene/board";
import { BG, triggerDownload, type ExportFocalOptions } from "./exportShared";

export { BG, type ExportFocalOptions } from "./exportShared";

/** modern-screenshot options shared by the canvas + svg capture: omit the
 *  export-chrome, neutralize the live pan/zoom transform so the frame sits
 *  at (0,0), and size the snapshot to the focal frame only (no padding —
 *  we composite that ourselves so the bg fills every output pixel). */
function captureOpts(focal: BoardSpace) {
  return {
    width: Math.max(1, focal.width),
    height: Math.max(1, focal.height),
    font: false as const,
    filter: (node: Node) =>
      !(node instanceof HTMLElement && node.hasAttribute("data-export-omit")),
    style: {
      transform: `translate(${-focal.originX}px, ${-focal.originY}px)`,
      transformOrigin: "0 0",
      willChange: "auto",
      // Safari's idle path scales via CSS `zoom` instead of the transform
      // (see BoardCanvas) — neutralize it too or the capture is scaled.
      zoom: "1",
    },
  };
}

/** Capture the focal frame and composite it onto a padded, bg-filled
 *  canvas. Shared by PNG download + clipboard copy. */
async function captureFocalCanvas(
  contentEl: HTMLElement,
  focal: BoardSpace,
  opts: ExportFocalOptions,
): Promise<HTMLCanvasElement> {
  const { scale = 1, padding = 96 } = opts;
  const innerCanvas = await domToCanvas(contentEl, {
    ...captureOpts(focal),
    scale,
    backgroundColor: BG,
  });

  const outW = Math.max(1, Math.round((focal.width + padding * 2) * scale));
  const outH = Math.max(1, Math.round((focal.height + padding * 2) * scale));
  const out = document.createElement("canvas");
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, outW, outH);
  ctx.drawImage(innerCanvas, Math.round(padding * scale), Math.round(padding * scale));
  return out;
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas produced no blob"))),
      type,
    ),
  );
}

export async function exportFocalAsPng(
  contentEl: HTMLElement,
  focal: BoardSpace,
  opts: ExportFocalOptions = {},
): Promise<void> {
  const { filename = "archlang-space.png" } = opts;
  const canvas = await captureFocalCanvas(contentEl, focal, opts);
  triggerDownload(await canvasToBlob(canvas, "image/png"), filename);
}

/** Whether this browser can write images to the clipboard at all. */
export function imageClipboardSupported(): boolean {
  return typeof ClipboardItem !== "undefined" && !!navigator.clipboard?.write;
}

/** Capture the focal frame as a PNG blob — the producer half of the
 *  clipboard copy. Split from the write so callers can start the write
 *  synchronously (see writeImageToClipboard). */
export async function captureFocalPngBlob(
  contentEl: HTMLElement,
  focal: BoardSpace,
  opts: ExportFocalOptions = {},
): Promise<Blob> {
  const canvas = await captureFocalCanvas(contentEl, focal, opts);
  return canvasToBlob(canvas, "image/png");
}

/**
 * Write a (pending) PNG to the clipboard.
 *
 * MUST be called synchronously inside the user gesture: Safari voids the
 * gesture at the first `await`, after which `clipboard.write` rejects with
 * NotAllowedError. The sanctioned async pattern is to hand ClipboardItem a
 * `Promise<Blob>` and invoke `write()` immediately — the browser then waits
 * for the blob itself while the gesture credit is preserved. So: no `await`
 * between the click handler and this call, anywhere up the chain.
 */
export function writeImageToClipboard(blob: Promise<Blob>): Promise<void> {
  if (!imageClipboardSupported()) {
    return Promise.reject(
      new Error("Copying images isn't supported in this browser"),
    );
  }
  return navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
}

